import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    if (this.dataset.variantMetafield) {
      const closestSection = this.closest('.shopify-section, dialog');
      if (closestSection) {
        closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
      }
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Single-open: close sibling rows when opening this one
    if (!this.details.open) {
      const container = this.closest('[data-single-open]');
      if (container) {
        container.querySelectorAll('accordion-custom').forEach((sibling) => {
          if (sibling !== this) sibling.details.open = false;
        });
      }
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }



  /**
   * Handles variant update events to refresh variant metafield content.
   * @param {Event} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const key = this.dataset.variantMetafield;
    if (!key) return;

    const newHtml = event.detail?.data?.html;
    if (!newHtml) return;

    const newAccordion = newHtml.querySelector(`accordion-custom[data-variant-metafield="${key}"]`);
    if (newAccordion) {
      const newContent = newAccordion.querySelector('.details-content');
      const currentContent = this.querySelector('.details-content');
      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
      this.style.display = '';
    } else {
      // Variant doesn't have this metafield — hide the row
      this.style.display = 'none';
    }
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-read-more-target]');
  if (!link) return;

  const targetId = link.dataset.readMoreTarget;
  const target = document.getElementById(targetId);
  if (!target) return;

  event.preventDefault();

  const details = target.querySelector('details');
  if (details && !details.open) {
    // Close siblings if single-open is enabled
    const container = target.closest('[data-single-open]');
    if (container) {
      container.querySelectorAll('accordion-custom').forEach((sibling) => {
        if (sibling !== target) {
          const siblingDetails = sibling.querySelector('details');
          if (siblingDetails) siblingDetails.open = false;
        }
      });
    }
    details.open = true;
  }

  // Delay scroll slightly so the accordion content expands and layout settles
  requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
});
